import { ScaleDefinition } from '../../models/dynamic-scale.model';

/**
 * A resolved scale field value: either a raw numeric score or a computed/string result.
 */
export type ScaleValue = string | number;

export class DynamicScaleCalculator {
  /**
   * Main entry point to evaluate a mathematical/logical expression from the JSON configuration.
   * @returns {ScaleValue} The computed value
   */
  static evaluateCalculation(
    calculation: string,
    data: ScaleDefinition,
    currentValues: Record<string, ScaleValue>
  ): ScaleValue {
    const sumKeys = data.scoreConfig?.scoreFields || [];
    let formula = calculation;
    formula = this.resolveGlobalAggregations(formula, sumKeys, currentValues);
    formula = this.resolveVariables(formula, currentValues);
    formula = this.resolveArrayFunctions(formula);
    formula = this.resolveMathFunctions(formula);

    const parsedIf = this.parseIfStatement(formula);
    return this.evaluateBasicMath(parsedIf);
  }

  /**
   * This function is used to resolve the global aggregations.
   */
  private static resolveGlobalAggregations(
    formula: string,
    sumKeys: string[],
    currentValues: Record<string, ScaleValue>
  ): string {
    const values = sumKeys.map(key => Number(currentValues[key]) || 0);
    const sum = values.reduce((acc, curr) => acc + curr, 0);
    const avg = values.length ? sum / values.length : 0;

    return formula
      .replace(/SUM\(scoreConfig\.(sumOf|scoreFields)\)/g, sum.toString())
      .replace(/AVG\(scoreConfig\.(sumOf|scoreFields)\)/g, avg.toString());
  }

  /**
   * This function is used to resolve the variables.
   */
  private static resolveVariables(
    formula: string,
    currentValues: Record<string, ScaleValue>
  ): string {
    return Object.keys(currentValues)
      .sort((a, b) => b.length - a.length)
      .reduce((accFormula, key) => {
        const val = currentValues[key] ?? 0;
        const regex = new RegExp(`\\b${key}\\b`, 'g');
        return accFormula.replace(regex, val.toString());
      }, formula);
  }
  /**
   * This function is used to parse the array functions.
   */
  private static resolveArrayFunctions(formula: string): string {
    let result = formula;
    const extractNumbers = (str: string) =>
      str.split(',').map(n => Number(n.trim()) || 0);

    result = result.replace(/SUM\(\[\s*([-\d.\s,]+)\s*\]\)/g, (_, match) => {
      const numbers = extractNumbers(match);
      return numbers.reduce((acc, curr) => acc + curr, 0).toString();
    });

    result = result.replace(/AVG\(\[\s*([-\d.\s,]+)\s*\]\)/g, (_, match) => {
      const numbers = extractNumbers(match);
      const sum = numbers.reduce((acc, curr) => acc + curr, 0);
      return (numbers.length ? sum / numbers.length : 0).toString();
    });

    result = result.replace(/MAX\(\[\s*([-\d.\s,]+)\s*\]\)/g, (_, match) => {
      const numbers = extractNumbers(match);
      return Math.max(...numbers).toString();
    });

    return result;
  }

  /**
   * This function is used to parse the math functions.
   */
  /**
   * This function is used to parse the math functions.
   */
  private static resolveMathFunctions(formula: string): string {
    return formula.replace(
      /ROUND\(\s*(.+?)\s*,\s*(\d+)\s*\)/g,
      (_, expressionStr, decStr) => {
        // 1. Valutiamo l'espressione matematica interna prima di arrotondare
        const evaluated = this.evaluateBasicMath(expressionStr);

        // 2. Ora applichiamo l'arrotondamento al risultato
        const val = Number(evaluated) || 0;
        const dec = Number(decStr) || 0;
        const multiplier = Math.pow(10, dec);
        return (Math.round(val * multiplier) / multiplier).toString();
      }
    );
  }
  /**
   * This function is used to parse the if statement.
   */
  private static parseIfStatement(expr: string): ScaleValue {
    const cleanedExpr = expr
      .replace(/\r?\n|\r/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    if (cleanedExpr.startsWith('IF(')) {
      const content = cleanedExpr.substring(3, cleanedExpr.length - 1);
      const commas = this.findTopLevelCommas(content);

      if (commas.first !== -1 && commas.second !== -1) {
        const conditionStr = content.substring(0, commas.first).trim();
        const trueStr = content
          .substring(commas.first + 1, commas.second)
          .trim();
        const falseStr = content.substring(commas.second + 1).trim();

        return this.evaluateCondition(conditionStr)
          ? this.parseIfStatement(trueStr)
          : this.parseIfStatement(falseStr);
      }
    }

    if (
      (cleanedExpr.startsWith('"') && cleanedExpr.endsWith('"')) ||
      (cleanedExpr.startsWith("'") && cleanedExpr.endsWith("'"))
    ) {
      return cleanedExpr.substring(1, cleanedExpr.length - 1);
    }

    const num = Number(cleanedExpr);
    return !isNaN(num) ? num : cleanedExpr;
  }

  /**
   * This function is used to find the first and second commas in a string
   */
  private static findTopLevelCommas(content: string): {
    first: number;
    second: number;
  } {
    return content.split('').reduce(
      (acc, char, index) => {
        const depth =
          char === '('
            ? acc.depth + 1
            : char === ')'
              ? acc.depth - 1
              : acc.depth;
        const isTopLevelComma = char === ',' && depth === 0;

        return {
          depth,
          first: isTopLevelComma && acc.first === -1 ? index : acc.first,
          second:
            isTopLevelComma && acc.first !== -1 && acc.second === -1
              ? index
              : acc.second,
        };
      },
      { depth: 0, first: -1, second: -1 }
    );
  }

  private static evaluateCondition(cond: string): boolean {
    const match = cond.match(/(.*?)(<=|>=|<|>|==|!=)(.*)/);
    if (!match) return false;

    const left = Number(this.evaluateBasicMath(match[1].trim()));
    const operator = match[2].trim();
    const right = Number(this.evaluateBasicMath(match[3].trim()));

    switch (operator) {
      case '<=':
        return left <= right;
      case '>=':
        return left >= right;
      case '<':
        return left < right;
      case '>':
        return left > right;
      case '==':
        return left === right;
      case '!=':
        return left !== right;
      default:
        return false;
    }
  }

  // --- 5. BASIC MATH EXECUTION ---

  private static evaluateBasicMath(expression: ScaleValue): ScaleValue {
    if (typeof expression === 'string' && /^[-+/*.\d\s()]+$/.test(expression)) {
      try {
        const result = new Function(`return ${expression}`)();
        return typeof result === 'number' && !isNaN(result) && isFinite(result)
          ? result
          : expression;
      } catch {
        return expression;
      }
    }
    return expression;
  }
}
